/**
 * PageSkeleton — the L1 (far-zoom) representation of a page node.
 *
 * Renders the page's REAL structure — its section blocks and their widget
 * layout — but as lightweight SVG <img> wireframes (one per section) instead of
 * mounting the real grids/widgets (no Lexical, images, forms). Each section is
 * positioned with the SAME grid math (`gridToPixel`) the live page grid uses, so
 * the wireframe occupies the same space and the page doesn't jump when zoom
 * crosses the threshold to the real <Grid>.
 *
 * The section drawings are the exact same ones the Add palette shows (svgPreview)
 * — so a "Hero" reads the same whether you're dragging it in or looking at it
 * placed here. As <img> the browser rasterizes each once (see svgPreview).
 */
import { html, css } from "react-strict-dom";
import { colors } from "@duro-app/tokens/tokens/colors.css";
import { borders } from "@duro-app/tokens/tokens/borders.css";
import { useGridStore, gridToPixel } from "@fredericrous/grid";
import { useThemeVars } from "./ThemeVarsContext.js";
import { sectionWireframe } from "./svgPreview.js";

const styles = css.create({
  // Dynamic: each far-zoom page has its own stack height + theme background.
  container: (height: number | string, background: string) => ({
    position: "relative",
    height,
    width: "100%",
    backgroundColor: background,
    // Skeleton is decorative — never intercept the pointer (section reorder is
    // disabled at this band anyway, and drag-to-add hit-tests the page).
    pointerEvents: "none",
  }),
  // Dynamic: each section sits at its own grid-derived pixel box.
  wireframe: (left: number, top: number, width: number, height: number) => ({
    position: "absolute",
    left,
    top,
    width,
    height,
    display: "block",
  }),
  // Shared header/footer band (in normal flow, above/below the body skeleton).
  regionBand: (height: number, edge: "top" | "bottom") => ({
    display: "block",
    width: "100%",
    height,
    flexShrink: 0,
    borderBottomWidth: edge === "top" ? borders.hairline : 0,
    borderTopWidth: edge === "bottom" ? borders.hairline : 0,
    borderStyle: "solid",
    borderColor: colors.border,
  }),
});

// Mirrors PAGE_GRID_CONFIG (the page grid the sections stack in).
const COLS = 12;
const ROW_H = 60;
const GAP = 8;
const PAD = 8;

interface SkeletonItem {
  id: string;
  position: { x: number; y: number; w: number; h: number };
  contentType?: string;
  nestedGridId?: string;
}

/** One section block, drawn from its live nested-grid items as an SVG <img>
 *  sized to the exact pixel box it occupies on the page. */
function SectionWireframe({
  nestedGridId,
  left,
  top,
  width,
  height,
}: {
  nestedGridId?: string;
  left: number;
  top: number;
  width: number;
  height: number;
}) {
  // Select the STABLE items record — zustand v5 / useSyncExternalStore needs a
  // cached snapshot, so the selector must NOT build a fresh array each call.
  const items = useGridStore((s) =>
    nestedGridId ? s.grids[nestedGridId]?.items : undefined,
  );
  const widgets = Object.values(items ?? {}) as SkeletonItem[];
  const { uri } = sectionWireframe(widgets, width, height);
  return (
    <html.img
      src={uri}
      alt=""
      draggable={false}
      style={styles.wireframe(left, top, width, height)}
    />
  );
}

/**
 * A shared header/footer band at far zoom — the region grid's widgets drawn as a
 * single SVG wireframe, sized to the pixel height its items occupy (same grid
 * math as the sections). Renders in normal flow above/below the body skeleton so
 * the wireframe page reads as a COMPLETE page (header · body · footer), matching
 * the real layout. Empty region → nothing to mock.
 */
export function RegionSkeleton({
  gridId,
  width,
  edge,
}: {
  gridId: string;
  width: number;
  edge: "top" | "bottom";
}) {
  const items = useGridStore((s) => s.grids[gridId]?.items);
  const widgets = Object.values(items ?? {}) as SkeletonItem[];
  if (widgets.length === 0) return null;
  let bottom = 0;
  for (const w of widgets) {
    const r = gridToPixel(w.position, width, COLS, ROW_H, GAP, PAD);
    if (r.top + r.height > bottom) bottom = r.top + r.height;
  }
  const height = bottom + PAD;
  const { uri } = sectionWireframe(widgets, width, height);
  return (
    <html.img
      src={uri}
      alt=""
      draggable={false}
      style={styles.regionBand(height, edge)}
    />
  );
}

export function PageSkeleton({
  pageGridId,
  width,
}: {
  pageGridId: string;
  /** The page body's pixel width (authoringWidth) — the grid math needs it to
   *  place sections; the body has no CSS to derive it from at far zoom. */
  width: number;
}) {
  const themeVars = useThemeVars();
  // Stable items record (see note above) → derive the section list inline.
  const items = useGridStore((s) => s.grids[pageGridId]?.items);
  const sections = (Object.values(items ?? {}) as SkeletonItem[])
    .filter((it) => it.contentType === "grid" && it.nestedGridId)
    .sort((a, b) => a.position.y - b.position.y);

  // Place each section with the grid's own math, tracking the stack's bottom.
  // The sections are ABSOLUTELY positioned (out of flow), so they contribute no
  // intrinsic height — the container must be given the full stack height itself,
  // or the page body stays at its min height and overflow:hidden clips every
  // section past the first. This total also mirrors the real grid's height, so
  // the node doesn't jump when zoom crosses back to the live <Grid>.
  let stackBottom = 0;
  const placed = sections.map((sec) => {
    const r = gridToPixel(sec.position, width, COLS, ROW_H, GAP, PAD);
    if (r.top + r.height > stackBottom) stackBottom = r.top + r.height;
    return { id: sec.id, nestedGridId: sec.nestedGridId, r };
  });

  // Resolve the theme background directly (the only consumer of the vars here —
  // children are self-contained SVG data-URIs), so no raw CSS-var spread is
  // needed and the container can be react-strict-dom.
  const background = themeVars["--wb-color-background"] ?? "#fff";
  const height = stackBottom > 0 ? stackBottom + PAD : "100%";

  return (
    <html.div style={styles.container(height, background)}>
      {placed.map(({ id, nestedGridId, r }) => (
        <SectionWireframe
          key={id}
          nestedGridId={nestedGridId}
          left={r.left}
          top={r.top}
          width={r.width}
          height={r.height}
        />
      ))}
    </html.div>
  );
}
